/**
 * HapticsManager - Capacitor haptics wrapper
 * Provides haptic feedback for native mobile platforms with graceful fallback.
 */

import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';

const STORAGE_KEY = 'zenith_haptics';

/**
 * Haptic impact strength levels
 */
export type HapticImpactStyle = 'light' | 'medium' | 'heavy';

/**
 * Notification haptic types
 */
export type HapticNotificationType = 'success' | 'warning' | 'error';

/**
 * Manages haptic feedback for the game
 */
export class HapticsManager {
  private enabled: boolean = true;
  private isNative: boolean;

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
    this.loadConfig();
  }

  /**
   * Trigger impact haptic feedback
   */
  async impact(style: HapticImpactStyle = 'medium'): Promise<void> {
    if (!this.enabled || !this.isNative) return;

    try {
      const styles: Record<HapticImpactStyle, ImpactStyle> = {
        light: ImpactStyle.Light,
        medium: ImpactStyle.Medium,
        heavy: ImpactStyle.Heavy,
      };

      await Haptics.impact({ style: styles[style] });
    } catch (e) {
      // Haptics may not be available on all devices
      console.debug('Haptics not available:', e);
    }
  }

  /**
   * Trigger notification haptic feedback
   */
  async notification(type: HapticNotificationType): Promise<void> {
    if (!this.enabled || !this.isNative) return;

    try {
      const types: Record<HapticNotificationType, NotificationType> = {
        success: NotificationType.Success,
        warning: NotificationType.Warning,
        error: NotificationType.Error,
      };

      await Haptics.notification({ type: types[type] });
    } catch (e) {
      console.debug('Haptics not available:', e);
    }
  }

  /**
   * Trigger vibration for a duration
   */
  async vibrate(duration: number = 100): Promise<void> {
    if (!this.enabled || !this.isNative) return;

    try {
      await Haptics.vibrate({ duration });
    } catch (e) {
      console.debug('Haptics not available:', e);
    }
  }

  // ============= Game Event Convenience Methods =============

  /**
   * Haptic for piece collision with stack
   */
  async impactCollision(): Promise<void> {
    await this.impact('medium');
  }

  /**
   * Light haptic tick for sliding feedback
   */
  async slideTick(): Promise<void> {
    await this.impact('light');
  }

  /**
   * Heavy haptic for locking piece in place
   */
  async lockPiece(): Promise<void> {
    await this.impact('heavy');
  }

  /**
   * Success notification for winning
   */
  async winCelebration(): Promise<void> {
    await this.notification('success');
  }

  /**
   * Error notification for losing
   */
  async loseFeedback(): Promise<void> {
    await this.notification('error');
  }

  /**
   * Light tap for UI interaction
   */
  async buttonTap(): Promise<void> {
    await this.impact('light');
  }

  // ============= Settings =============

  /**
   * Enable or disable haptics
   */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.saveConfig();
  }

  /**
   * Check if haptics are enabled
   */
  isEnabled(): boolean {
    return this.enabled;
  }

  /**
   * Toggle haptics on/off
   */
  toggle(): boolean {
    this.enabled = !this.enabled;
    this.saveConfig();
    return this.enabled;
  }

  /**
   * Check if running on native platform
   */
  isNativePlatform(): boolean {
    return this.isNative;
  }

  // ============= Persistence =============

  /**
   * Load config from localStorage
   */
  private loadConfig(): void {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        this.enabled = parsed.enabled ?? true;
      }
    } catch (e) {
      console.warn('Failed to load haptics config:', e);
    }
  }

  /**
   * Save config to localStorage
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ enabled: this.enabled }));
    } catch (e) {
      console.warn('Failed to save haptics config:', e);
    }
  }
}
